import { useParams, Navigate, Link } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import Reveal from "../components/Reveal";
import Icon from "../components/Icons";
import CtaBand from "../components/CtaBand";
import styles from "./ServicePage.module.css";
import {
  findService,
  servicesByCategory,
  categories,
} from "../data/services";

/** A single physiotherapy service. Driven entirely by the slug. */
export default function ServicePage() {
  const { slug } = useParams();
  const service = findService(slug);

  // unknown slug falls through to the 404 route
  if (!service || service.category !== "physiotherapy") {
    return <Navigate to="/404" replace />;
  }

  const siblings = servicesByCategory("physiotherapy").filter(
    (s) => s.slug !== slug
  );

  return (
    <>
      <PageHeader
        eyebrow={categories.physiotherapy.label}
        title={service.title}
        lead={service.short}
      />

      <section className={`u-section ${styles.section}`}>
        <div className={`u-shell ${styles.grid}`}>
          <Reveal className={styles.main}>
            <p className={styles.intro}>{service.intro}</p>

            {service.helps?.length > 0 && (
              <div className={styles.block}>
                <h2 className={styles.blockTitle}>What this helps with</h2>
                <ul className={styles.helps}>
                  {service.helps.map((h) => (
                    <li key={h}>
                      <span className={styles.bullet} aria-hidden="true" />
                      {h}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {service.expect?.length > 0 && (
              <div className={styles.block}>
                <h2 className={styles.blockTitle}>What to expect</h2>
                <ol className={styles.steps}>
                  {service.expect.map((step, i) => (
                    <li key={step} className={styles.step}>
                      <span className={styles.stepNum}>{i + 1}</span>
                      <span>{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </Reveal>

          <Reveal className={styles.aside} delay={90}>
            <div className={styles.asideCard}>
              <span className={styles.asideIcon}>
                <Icon name={service.icon} size={26} />
              </span>
              <h2 className={styles.asideTitle}>Not sure this is your issue?</h2>
              <p className={styles.asideText}>
                That is what the assessment is for. Describe your symptoms and
                I will tell you honestly whether this is the right treatment —
                or point you somewhere better suited.
              </p>
              <Link to="/contact" className={styles.asideCta}>
                Book an assessment
                <Icon name="arrow" size={17} />
              </Link>
            </div>

            <nav className={styles.related} aria-label="Other physiotherapy services">
              <h2 className={styles.relatedTitle}>Other physiotherapy services</h2>
              <ul>
                {siblings.map((s) => (
                  <li key={s.slug}>
                    <Link
                      to={`/physiotherapy/${s.slug}`}
                      className={styles.relatedLink}
                    >
                      <Icon name={s.icon} size={18} />
                      <span>{s.title}</span>
                      <Icon
                        name="chevronRight"
                        size={15}
                        className={styles.relatedArrow}
                      />
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          </Reveal>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
